import { OrganizationType } from '../types/organization.type';
import type { KnowledgeArticle } from '../types/knowledge-base.type';

const RECYCLER_ONLY = [OrganizationType.RECYCLER] as const;

export const KNOWLEDGE_BASE_ARTICLES: KnowledgeArticle[] = [
  {
    id: 'supply',
    title: 'Supply Targets',
    summary: 'Set weekly or monthly material intake targets for your recycler organization.',
    content:
      'The Supply page lets you define how much waste material your organization expects to receive. Targets drive fulfillment tracking on the Dashboard.\n\nSelect a guide below for step-by-step instructions.',
    category: 'supply_targets',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['supply', 'targets', 'appetite'],
    children: [
      'supply-overview',
      'supply-material-lines',
      'supply-accepts-extra',
    ],
  },
  {
    id: 'supply-overview',
    title: 'Supply Overview',
    summary: 'Navigate, configure, and save your supply targets.',
    content:
      '1. Open Supply from the sidebar.\n\n2. Select your recycler organization from the dropdown at the top of the sidebar.\n\n3. Use the Weekly / Monthly toggle above the material table to set your target period.\n\n4. Review and edit material lines in the table.\n\n5. Click Save in the page header (top right) and wait for the success confirmation.\n\n6. Open Dashboard from the sidebar to verify fulfillment reflects your saved targets.\n\nTip: If the page is empty, add at least one material line with a waste category and quantity. If save fails, check that every line has a category and a quantity greater than zero.',
    category: 'supply_targets',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['supply', 'overview'],
  },
  {
    id: 'supply-material-lines',
    title: 'Material Lines',
    summary: 'Add waste categories, quantities, and units.',
    content:
      '1. On the Supply page, click Add line to create a new material row.\n\n2. Select a waste category from the dropdown (categories come from your organization materials).\n\n3. Enter the target quantity for the selected period.\n\n4. Choose a unit (for example kg or ton).\n\n5. Remove a line with the delete icon when it is no longer needed.\n\n6. Click Save in the page header when finished.\n\nTip: You need at least one complete line (category, quantity, unit) before saving.',
    category: 'supply_targets',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['supply', 'materials', 'categories', 'units'],
  },
  {
    id: 'supply-accepts-extra',
    title: 'Accept Extra Deliveries',
    summary: 'Allow or block intake above your stated targets.',
    content:
      '1. On the Supply page, find the Accept extra deliveries switch.\n\n2. When enabled, over-target deliveries are allowed and shown as warnings on the Dashboard.\n\n3. When disabled, excess quantities that exceed targets may be flagged as not allowed on the Dashboard.\n\n4. Click Save in the page header after changing this setting.\n\nBest practice: Enable this only if your facility can handle variable intake volumes.',
    category: 'supply_targets',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['supply', 'extra', 'over-target'],
  },
  {
    id: 'dashboard',
    title: 'Fulfillment Dashboard',
    summary: 'Track received material against your supply targets.',
    content:
      'The Dashboard compares material received against the targets you set on Supply. Summary cards and progress bars show month and week performance in one place.\n\nSelect a guide below for step-by-step instructions.',
    category: 'dashboard_fulfillment',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['dashboard', 'fulfillment', 'targets'],
    children: [
      'dashboard-overview',
      'dashboard-fulfillment-status',
      'dashboard-empty-state',
    ],
  },
  {
    id: 'dashboard-overview',
    title: 'Dashboard Overview',
    summary: 'Understand fulfillment metrics at a glance.',
    content:
      '1. Open Dashboard from the sidebar.\n\n2. Confirm your recycler organization is selected in the sidebar dropdown.\n\n3. A chip in the page header shows whether your saved targets use a weekly or monthly period.\n\n4. Review the summary cards for target kg, received kg, and overall fulfillment for the active period.\n\n5. Switch between Month and Week using the tabs below the page header.\n\n6. Use Refresh in the page header to reload data, or Edit Supply to update targets.',
    category: 'dashboard_fulfillment',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['dashboard', 'overview'],
  },
  {
    id: 'dashboard-fulfillment-status',
    title: 'Fulfillment Status',
    summary: 'Interpret progress bars and over-target indicators.',
    content:
      'Each material row includes a progress bar showing fulfillment percentage.\n\nWhen received kg exceeds target kg, a status chip may appear:\n\n• Over target — delivery exceeded the target; shown when extra deliveries are accepted.\n• Excess not allowed — delivery exceeded the target but your Supply settings do not accept extra quantity.\n\nProgress bar colors reflect fulfillment status. Check your Accept extra deliveries toggle on Supply if statuses look unexpected.',
    category: 'dashboard_fulfillment',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['dashboard', 'status', 'over-target'],
  },
  {
    id: 'dashboard-empty-state',
    title: 'No Fulfillment Data',
    summary: 'What to do when the dashboard shows no lines.',
    content:
      '1. If the Dashboard shows no fulfillment lines, your targets may not be configured yet.\n\n2. Click Edit Supply in the page header (or open Supply from the sidebar).\n\n3. Add material lines and click Save in the page header.\n\n4. Return to the Dashboard and click Refresh.\n\nTip: Fulfillment data appears after targets are saved and matching deliveries are recorded for your organization.',
    category: 'dashboard_fulfillment',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['dashboard', 'empty', 'setup'],
  },
  {
    id: 'orders',
    title: 'Orders',
    summary: 'Create and manage recycler pre-orders shown in the marketplace.',
    content:
      'The Orders page lets you review summary metrics, search and filter pre-orders, and create new orders for your recycler organization. Orders appear in Marketplace for matching supply.\n\nSelect a guide below for step-by-step instructions.',
    category: 'orders',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['orders', 'pre-order', 'marketplace'],
    children: [
      'orders-overview',
      'orders-summary',
      'orders-search-filter',
      'orders-create',
      'orders-table',
    ],
  },
  {
    id: 'orders-overview',
    title: 'Orders Overview',
    summary: 'Navigate the Orders page from summary cards to the create dialog.',
    content:
      '1. Open Orders from the sidebar.\n\n2. Confirm your recycler organization is selected in the sidebar dropdown.\n\n3. Review the summary cards for total orders, pending, partially fulfilled, and total amount.\n\n4. Use Search and Status filters to narrow the list.\n\n5. Browse the orders table for category, amount, fulfillment, and status.\n\n6. Click Create Order in the page header to open the create dialog.\n\nTip: You need create permission on pre-orders to add a new order.',
    category: 'orders',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['orders', 'overview'],
  },
  {
    id: 'orders-summary',
    title: 'Orders Summary Cards',
    summary: 'Read total, pending, partial, and amount metrics at a glance.',
    content:
      '1. At the top of Orders, four cards summarize your organization\'s pre-orders.\n\n2. Total orders — count of all listed pre-orders.\n\n3. Pending — orders waiting to be fulfilled.\n\n4. Partially fulfilled — orders with partial delivery progress.\n\n5. Total amount — sum of order amounts (display units may vary by order).\n\nTip: Cards update when you create or remove orders, or after data reloads.',
    category: 'orders',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['orders', 'summary', 'metrics'],
  },
  {
    id: 'orders-search-filter',
    title: 'Search and Filter Orders',
    summary: 'Find orders by text or status.',
    content:
      '1. In the filter bar below the summary cards, use Search orders to match ID, material category, reference, or notes.\n\n2. Use the Status dropdown to show all statuses or a specific status (for example Pending).\n\n3. The table updates immediately to show matching rows.\n\nTip: Clear search text and set Status to All statuses to see the full list again.',
    category: 'orders',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['orders', 'search', 'filter', 'status'],
  },
  {
    id: 'orders-create',
    title: 'Create an Order',
    summary: 'Open Create Order and submit a new pre-order.',
    content:
      '1. Click Create Order in the Orders page header.\n\n2. Select a material category from the list (categories come from your organization materials).\n\n3. Enter an amount greater than zero and choose a unit.\n\n4. Optionally add a reference and notes.\n\n5. Click Create Order in the dialog to submit, or Cancel to close without saving.\n\nTip: New orders start as Pending. If create is disabled, your role may not have pre-order create permission.',
    category: 'orders',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['orders', 'create', 'pre-order'],
  },
  {
    id: 'orders-table',
    title: 'Orders Table',
    summary: 'Review each pre-order and delete pending ones when allowed.',
    content:
      '1. The table lists Order ID, material category, amount, fulfillment percent, status, reference, created date, and actions.\n\n2. Status chips show Pending, Partially fulfilled, Completed, and related states.\n\n3. For Pending orders, use the delete action when your role allows it.\n\n4. If no rows match, adjust search or status filters.\n\nTip: Fulfillment percent reflects progress against the ordered amount when available.',
    category: 'orders',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['orders', 'table', 'delete'],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    summary: 'View alerts and manage read status.',
    content:
      'Notifications keep you informed about platform activity. Open Notifications from the sidebar footer to review unread items on a dedicated page.\n\nSelect a guide below for step-by-step instructions.',
    category: 'technical_support',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['notifications', 'alerts'],
    children: ['notification-page', 'mark-notifications-read'],
  },
  {
    id: 'notification-page',
    title: 'Open Notifications',
    summary: 'Browse notifications from the sidebar.',
    content:
      '1. Click Notifications in the sidebar footer (above Help).\n\n2. The badge on the icon shows how many unread notifications you have.\n\n3. The notifications page opens with Unread and Read tabs below the page header.\n\n4. Scroll the list to review recent alerts for your organization.\n\nNotifications are scoped to the organization currently selected in the sidebar.',
    category: 'technical_support',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['notifications', 'sidebar'],
  },
  {
    id: 'mark-notifications-read',
    title: 'Mark Notifications as Read',
    summary: 'Clear unread items individually or in bulk.',
    content:
      '1. Open Notifications from the sidebar.\n\n2. On the Unread tab, click a notification to mark it as read — it moves to the Read tab.\n\n3. Use Mark all as read in the page header to clear every unread notification at once.\n\n4. The sidebar badge count updates immediately after items are marked read.',
    category: 'technical_support',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['notifications', 'read'],
  },
  {
    id: 'account-settings',
    title: 'Account & Preferences',
    summary: 'Change the interface language, switch theme, and manage your account.',
    content:
      'Open the account menu from your profile at the bottom of the sidebar to change the interface language, switch between light and dark mode, and sign out. Your profile shows your username and active organization.\n\nSelect a guide below for step-by-step instructions.',
    category: 'technical_support',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['account', 'language', 'theme', 'appearance', 'preferences', 'settings'],
    children: ['change-language', 'appearance-theme'],
  },
  {
    id: 'change-language',
    title: 'Change Language',
    summary: 'Switch the platform language across the supported languages.',
    content:
      '1. Click your profile at the bottom of the sidebar to open the account menu.\n\n2. Click Languages.\n\n3. Choose your language from the list: English, Arabic, Urdu, Hindi, Bengali, Sinhala, or Nepali.\n\n4. The interface updates instantly, and your choice is remembered the next time you sign in.\n\nNote: Arabic and Urdu automatically switch the layout to right-to-left (RTL) for comfortable reading. Help articles are available in all supported languages.',
    category: 'technical_support',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['language', 'arabic', 'urdu', 'rtl', 'localization', 'translation'],
  },
  {
    id: 'appearance-theme',
    title: 'Light & Dark Mode',
    summary: 'Switch between light and dark appearance.',
    content:
      '1. Click your profile at the bottom of the sidebar to open the account menu.\n\n2. Use the Dark mode switch to toggle between the light and dark themes.\n\n3. Your preference is saved automatically and applied on every visit.',
    category: 'technical_support',
    organizationTypes: [...RECYCLER_ONLY],
    tags: ['theme', 'dark mode', 'light mode', 'appearance'],
  },
];

const CHILD_ARTICLE_IDS = new Set(
  KNOWLEDGE_BASE_ARTICLES.flatMap((article) => article.children ?? [])
);

export const getKnowledgeArticlesForOrganizationType = (
  organizationType: OrganizationType
): KnowledgeArticle[] =>
  KNOWLEDGE_BASE_ARTICLES.filter((article) => article.organizationTypes.includes(organizationType));

export const getTopLevelKnowledgeArticlesForOrganizationType = (
  organizationType: OrganizationType
): KnowledgeArticle[] =>
  getKnowledgeArticlesForOrganizationType(organizationType).filter(
    (article) => !CHILD_ARTICLE_IDS.has(article.id)
  );

export const getKnowledgeArticleById = (id: string): KnowledgeArticle | undefined =>
  KNOWLEDGE_BASE_ARTICLES.find((article) => article.id === id);

export const getKnowledgeArticleChildren = (
  article: KnowledgeArticle,
  organizationType: OrganizationType
): KnowledgeArticle[] => {
  if (!article.children?.length) return [];
  const allowed = new Set(
    getKnowledgeArticlesForOrganizationType(organizationType).map((item) => item.id)
  );
  return article.children
    .map((childId) => getKnowledgeArticleById(childId))
    .filter((child): child is KnowledgeArticle => Boolean(child && allowed.has(child.id)));
};

export const getKnowledgeArticleParentId = (articleId: string): string | undefined => {
  const parent = KNOWLEDGE_BASE_ARTICLES.find((article) => article.children?.includes(articleId));
  return parent?.id;
};
